import React, {useState} from 'react';
import { useTranslation } from 'react-i18next';
import { trackFormSubmission } from '../utils/analytics';
import { submitContactForm, validateContactForm } from '../utils/services';
import { FORM_CONFIG, SECTION_IDS } from '../utils/constants';

const Contact = () => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        [FORM_CONFIG.CONTACT_FORM.FIELDS.NAME]: '',
        [FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL]: '',
        [FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE]: ''
    });

    const [formStatus, setFormStatus] = useState({
        submitted: false,
        error: false,
        submitting: false,
        errorMessage: ''
    });

    const [formErrors, setFormErrors] = useState({
        [FORM_CONFIG.CONTACT_FORM.FIELDS.NAME]: '',
        [FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL]: '',
        [FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE]: ''
    });

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Reset previous errors
        setFormErrors({
            [FORM_CONFIG.CONTACT_FORM.FIELDS.NAME]: '',
            [FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL]: '',
            [FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE]: ''
        });

        // Validate form data
        const validation = validateContactForm(formData, t);

        if (!validation.isValid) {
            // Set validation errors
            setFormErrors(validation.errors);
            return;
        }

        // Set submitting status
        setFormStatus({
            ...formStatus,
            submitting: true,
            error: false,
            errorMessage: ''
        });

        try {
            // Track form submission attempt
            trackFormSubmission('Contact Form', { 
                hasName: !!formData.name.trim(),
                hasEmail: !!formData.email.trim(),
                hasMessage: !!formData.message.trim()
            });

            // Submit form data to external service
            await submitContactForm(formData);

            // Set success status
            setFormStatus({
                submitted: true,
                error: false,
                submitting: false,
                errorMessage: ''
            });

            // Reset form after successful submission
            setFormData({
                name: '',
                email: '',
                message: ''
            });
        } catch (error) {
            // Set error status
            setFormStatus({
                submitted: false,
                error: true,
                submitting: false,
                errorMessage: t('contact.defaultError')
            });

            console.error('Form submission error:', error);
        }
    };

    return (
        <section id={SECTION_IDS.CONTACT} className="py-12 sm:py-16 bg-grey-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center">{t('contact.title')}</h2>
                <p className="text-gray-600 mb-6 sm:mb-8 text-center">{t('contact.description')}</p>

                {formStatus.submitted ? (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6"
                         role="alert">
                        <strong className="font-bold">{t('contact.success').split('!')[0]}!</strong>
                        <span className="block sm:inline"> {t('contact.success').split('!')[1]}</span>
                    </div>
                ) : null}

                {formStatus.error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6"
                         role="alert">
                        <strong className="font-bold">{t('contact.error')}</strong>
                        <span className="block sm:inline"> {formStatus.errorMessage}</span>
                    </div>
                ) : null}

                <div className="bg-white rounded-lg p-4 sm:p-6 md:p-8 shadow-sm">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4 sm:mb-6">
                            <label htmlFor={FORM_CONFIG.CONTACT_FORM.FIELDS.NAME} className="block text-sm font-medium text-gray-700 mb-2">
                                {t('contact.form.name.label')}
                            </label>
                            <input
                                type="text"
                                id={FORM_CONFIG.CONTACT_FORM.FIELDS.NAME}
                                name={FORM_CONFIG.CONTACT_FORM.FIELDS.NAME}
                                value={formData[FORM_CONFIG.CONTACT_FORM.FIELDS.NAME]}
                                onChange={handleChange}
                                required
                                className={`w-full border ${formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.NAME] ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2 focus:ring-2 focus:ring-pastita-orange focus:border-transparent`}
                                placeholder={t('contact.form.name.placeholder')}
                            />
                            {formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.NAME] && (
                                <p className="text-red-500 text-xs mt-1">{formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.NAME]}</p>
                            )}
                        </div>

                        <div className="mb-4 sm:mb-6">
                            <label htmlFor={FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL}
                                   className="block text-sm font-medium text-gray-700 mb-2">
                                {t('contact.form.email.label')}
                            </label>
                            <input
                                type="email"
                                id={FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL}
                                name={FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL}
                                value={formData[FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL]}
                                onChange={handleChange}
                                required
                                className={`w-full border ${formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL] ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2 focus:ring-2 focus:ring-pastita-orange focus:border-transparent`}
                                placeholder={t('contact.form.email.placeholder')}
                            />
                            {formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL] && (
                                <p className="text-red-500 text-xs mt-1">{formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.EMAIL]}</p>
                            )}
                        </div>

                        <div className="mb-4 sm:mb-6">
                            <label htmlFor={FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE}
                                   className="block text-sm font-medium text-gray-700 mb-2">
                                {t('contact.form.message.label')}
                            </label>
                            <textarea
                                id={FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE}
                                name={FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE}
                                value={formData[FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE]}
                                onChange={handleChange}
                                required
                                rows="5"
                                className={`w-full border ${formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE] ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-2 focus:ring-2 focus:ring-pastita-orange focus:border-transparent`}
                                placeholder={t('contact.form.message.placeholder')}
                            ></textarea>
                            {formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE] && (
                                <p className="text-red-500 text-xs mt-1">{formErrors[FORM_CONFIG.CONTACT_FORM.FIELDS.MESSAGE]}</p>
                            )}
                        </div>

                        <div className="text-center">
                            <button
                                type="submit"
                                disabled={formStatus.submitting}
                                className={`w-full sm:w-auto bg-pastita-orange text-white px-6 sm:px-8 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm sm:text-base ${formStatus.submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {formStatus.submitting ? t('common.buttons.sending') : t('common.buttons.send')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
